/**
 * จู๋หมิง 竹鳴灣 (อ่าวไผ่ขาน), the 1 km starter region (docs/design/OW_1KM.md, the plan docs/review/open-world/ow-1km-plan.png,
 * approved 2026-10-08): option B's region (docs/archive/OPEN_WORLD.md) at one third, the schools left out, the five warp points
 * standing but locked ("ยังไม่เปิด"), and the village of layout ข (data/layouts/yanyu.ts, 160 m) stamped at its real size
 * on the north coast between the two river mouths.
 *
 * The shapes are `scripts/layouts/open-world/region-1km.mjs`'s, written here in its design units (0..3,072) and scaled to metres
 * by `ZHUMING_UNIT` (1/3) below; `unit` tells the sketch engine (region/sketch.ts) to scale its own constants the same
 * way, so the ground reads as the plan does. Heights scale too (the summit 380 → 127 m).
 *
 * Metres in the region: x east, y south, the sea north. Data only.
 */
import type { BlockoutBox, RegionSketch, RegionWild, XY } from '../../region/sketch';

/** Metres per design unit. */
export const ZHUMING_UNIT = 1 / 3;
const U = ZHUMING_UNIT;
const m = (v: number): number => Math.round(v * U * 100) / 100;
const xy = (p: XY): XY => [m(p[0]), m(p[1])];

/** The village's tile (0, 0) in the region (metres): design (1345, 352). Whole metres, so the stamp is byte for byte. */
export const ZHUMING_CORE = { x: 448, y: 117, size: 160 } as const;

/** The design sketch (region-1km.mjs), in design units: every length and height is scaled by `U` on the way out. */
const D = {
  coast: [
    [0, 360], [220, 330], [480, 350], [700, 320], [860, 280], [960, 190], [1040, 110], [1130, 90], [1210, 130],
    [1260, 230], [1320, 330], [1345, 400], [1825, 400], [1880, 440], [1960, 400], [2060, 330], [2150, 220],
    [2230, 130], [2320, 100], [2400, 140], [2460, 230], [2560, 300], [2760, 330], [3072, 300],
  ] as XY[],
  islands: [
    { x: 1660, y: 300, r: 26, h: 9, name: 'หินกลางอ่าว' },
    { x: 1300, y: 200, r: 70, h: 22, name: 'เกาะสนสามต้น' },
    { x: 2010, y: 170, r: 120, h: 34, name: 'เกาะอ่าวโจรสลัด' },
    { x: 1760, y: 120, r: 46, h: 16, name: 'เกาะประภาคาร' },
    { x: 2600, y: 150, r: 60, h: 18, name: 'เกาะเต่า' },
    { x: 520, y: 210, r: 80, h: 26, name: 'เกาะเมฆขาว' },
  ],
  stoneLegs: [
    [[1560, 400], [1660, 300]],
    [[1660, 300], [1760, 120]],
    [[1660, 300], [1300, 200]],
    [[1760, 120], [2010, 170]],
  ] as (readonly [XY, XY])[],
  peaks: [
    // the western range (ยอดเขาทอง first)
    { x: 520, y: 1000, h: 380, r: 330 },
    { x: 330, y: 760, h: 300, r: 260 },
    { x: 720, y: 1290, h: 250, r: 260 },
    { x: 250, y: 1320, h: 290, r: 300 },
    { x: 160, y: 520, h: 230, r: 260 },
    { x: 880, y: 700, h: 170, r: 240 },
    // the coastal range from the village's western cliffs to the west headland
    { x: 1180, y: 330, h: 70, r: 170 },
    { x: 1080, y: 160, h: 60, r: 120 },
    { x: 1300, y: 480, h: 52, r: 120 },
    // pine hills east of the village and the bamboo hills
    { x: 1720, y: 640, h: 48, r: 200 },
    { x: 1960, y: 820, h: 60, r: 220 },
    { x: 2200, y: 960, h: 72, r: 230 },
    { x: 2120, y: 640, h: 50, r: 170 },
    // east headland
    { x: 2330, y: 200, h: 52, r: 130 },
    // eastern ranges round the lake valley
    { x: 2900, y: 900, h: 190, r: 330 },
    { x: 2950, y: 1500, h: 170, r: 320 },
    { x: 2880, y: 2050, h: 180, r: 320 },
    { x: 2660, y: 1180, h: 110, r: 180 },
    // low hills between the village and the town
    { x: 1220, y: 880, h: 40, r: 200 },
    { x: 1760, y: 1180, h: 34, r: 220 },
    // bandit hills south-west of the town
    { x: 900, y: 1960, h: 95, r: 260 },
    { x: 640, y: 2240, h: 130, r: 320 },
    { x: 300, y: 1900, h: 200, r: 330 },
    // south of the town
    { x: 2050, y: 2250, h: 70, r: 260 },
    { x: 1150, y: 2450, h: 80, r: 260 },
    // the red cliff (south-east)
    { x: 2700, y: 2620, h: 210, r: 220 },
  ],
  southRidge: {
    pts: [[0, 2880], [500, 2860], [900, 2920], [1300, 2950], [1700, 2930], [2100, 2880], [2500, 2920], [3072, 2880]] as XY[],
    h: 250,
    reach: 340,
    edgeFrom: 2700,
  },
  flats: [
    { x: 1450, y: 1650, r: 330, h: 22, name: 'hub' },
    { x: 1480, y: 1050, r: 260, h: 17, name: 'midway fields' },
    { x: 1500, y: 760, r: 200, h: 12, name: 'south of the village' },
    { x: 2350, y: 1520, r: 420, h: 34, name: 'lake valley' },
    { x: 960, y: 820, r: 120, h: 40, name: 'foot village' },
    { x: 1100, y: 1180, r: 230, h: 42, name: 'stone forest floor' },
    { x: 1160, y: 1830, r: 150, h: 18, name: 'reed marsh' },
  ],
  rivers: [
    {
      name: 'ลำธารเมฆา',
      pts: [[640, 820], [760, 760], [900, 700], [1020, 620], [1120, 560], [1220, 500], [1290, 400], [1320, 330]] as XY[],
      bed: [150, 40, 30, 22, 14, 8, 2, -1],
      w: [10, 14, 16, 18, 20, 22, 26, 30],
    },
    {
      name: 'แม่น้ำบุปผา',
      pts: [
        [2250, 2850], [2350, 2660], [2280, 2460], [2370, 2260], [2320, 2060], [2400, 1860], [2380, 1650], [2370, 1380],
        [2300, 1250], [2180, 1150], [2120, 1040], [2020, 960], [2040, 840], [1960, 760], [1920, 640], [1860, 540], [1840, 450],
      ] as XY[],
      bed: [140, 90, 66, 50, 40, 33, 30, 29, 26, 21, 18, 15, 12, 9, 5, 2, -1],
      w: [10, 12, 14, 16, 18, 20, 20, 24, 26, 28, 28, 30, 32, 34, 36, 40, 46],
    },
    {
      name: 'ลำน้ำศิลา',
      pts: [[1720, 2620], [1640, 2420], [1720, 2220], [1650, 2020], [1730, 1830], [1800, 1600], [1860, 1400], [1920, 1200], [1990, 1060], [2020, 960]] as XY[],
      bed: [70, 40, 28, 22, 19, 17.5, 16.5, 15.8, 15.3, 15],
      w: [8, 10, 12, 13, 14, 15, 16, 17, 18, 20],
    },
  ],
  falls: [
    { x: 690, y: 790, drop: 105, name: 'น้ำตกเมฆา' },
    { x: 2330, y: 2160, drop: 30, name: 'น้ำตกบุปผา' },
    { x: 2620, y: 1440, drop: 60, name: 'น้ำตกหน้าผาตะวันออก' },
  ],
  lakes: [
    { x: 2400, y: 1520, rx: 230, ry: 150, h: 30, name: 'ทะเลสาบบุปผา' },
    { x: 610, y: 890, rx: 40, ry: 30, h: 148, name: 'บึงเมฆ' },
    { x: 1160, y: 1830, rx: 110, ry: 70, h: 17.5, name: 'บึงกก', marsh: true },
  ],
  cover: [
    ['bamboo', 2030, 860, 330],
    ['bamboo', 2240, 1060, 200],
    ['bamboo', 1060, 1020, 140],
    ['pine', 1700, 620, 210],
    ['farm', 1470, 1080, 250],
    ['farm', 1470, 800, 150],
    ['farm', 1700, 1700, 200],
    ['farm', 1230, 1500, 150],
    ['tea', 880, 940, 170],
    ['flower', 2210, 1700, 170],
    ['flower', 2560, 1700, 140],
    ['blossom', 2180, 1400, 110],
    ['blossom', 2620, 1350, 90],
    ['forest', 1100, 2150, 260],
    ['forest', 1900, 2000, 250],
    ['forest', 1250, 620, 140],
    ['redrock', 2700, 2620, 230],
  ] as RegionSketch['cover'][number][],
  roads: [
    { id: 'south', name: 'ถนนใต้ จู๋หมิง–ลานศิลา', main: true, pts: [[1587, 840], [1520, 940], [1500, 1050], [1470, 1250], [1452, 1470]] as XY[] },
    { id: 'westpass', name: 'ถนนช่องเขาตะวันตก', main: true, pts: [[1340, 525], [1250, 560], [1150, 600], [1060, 690], [960, 790]] as XY[] },
    { id: 'summit', name: 'ทางขึ้นยอดเขาทอง', pts: [[960, 790], [820, 880], [680, 960], [560, 1010]] as XY[] },
    { id: 'stoneforest', name: 'ถนนป่าเสาหิน', main: true, pts: [[1300, 1600], [1180, 1450], [1060, 1330], [1000, 1150], [960, 1000], [960, 790]] as XY[] },
    { id: 'east', name: 'ถนนตะวันออก', main: true, pts: [[1610, 1650], [1800, 1610], [1950, 1580], [2100, 1560], [2160, 1560]] as XY[] },
    { id: 'southpass', name: 'ถนนใต้ ลานศิลา–ด่านใต้', main: true, pts: [[1450, 1800], [1440, 2100], [1470, 2450], [1470, 2900]] as XY[] },
    { id: 'redcliff', name: 'ถนนผาแดง', main: true, pts: [[2200, 1650], [2300, 1900], [2450, 2300], [2560, 2880]] as XY[] },
    { id: 'southwest', name: 'ถนนตะวันตกเฉียงใต้', main: true, pts: [[1300, 1700], [1100, 1900], [880, 2100], [500, 2250], [60, 2300]] as XY[] },
    { id: 'northpass', name: 'ทางช่องเขาเหนือ', pts: [[700, 1000], [520, 870], [330, 700], [60, 620]] as XY[] },
    { id: 'eastcoast', name: 'ทางเลียบผาตะวันออก', pts: [[1560, 430], [1700, 500], [1850, 480], [1990, 420], [2120, 300], [2280, 200]] as XY[] },
    { id: 'bamboo', name: 'ทางทะเลไผ่', pts: [[1500, 1050], [1700, 1000], [1900, 960], [2100, 1100], [2250, 1250], [2250, 1440]] as XY[] },
    { id: 'headland', name: 'ทางผาหัวมังกร', pts: [[1250, 520], [1200, 380], [1120, 220], [1080, 140]] as XY[] },
  ],
  // The plan's `town(...)` grounds.
  grounds: [
    { x: 1450, y: 1650, rw: 150, rh: 130, round: true, cover: 'town' }, // ลานศิลาเก้ากระบี่ inside its walls
    { x: 960, y: 820, rw: 60, rh: 45, cover: 'town' }, // หมู่บ้านเชิงเขา
    { x: 2150, y: 1560, rw: 70, rh: 55, cover: 'town' }, // the lakeside town
    { x: 1500, y: 1060, rw: 22, rh: 18, cover: 'town' }, // ศาลาพักม้าสิบลี้
    { x: 1490, y: 2840, rw: 40, rh: 26, cover: 'town' }, // the south pass fort
    { x: 860, y: 1980, rw: 50, rh: 40, cover: 'camp' }, // ค่ายโจรภูเขา
    { x: 1650, y: 2330, rw: 55, rh: 45, cover: 'ruin' }, // ซากวัดกระบี่ทมิฬ
    { x: 1080, y: 140, rw: 30, rh: 24, cover: 'ruin' }, // ป้อมร้างผาหัวมังกร
    { x: 2010, y: 190, rw: 35, rh: 25, cover: 'camp' }, // อ่าวโจรสลัด
    { x: 2230, y: 980, rw: 26, rh: 22, cover: 'ruin' }, // วัดร้างในป่าไผ่
  ] as RegionSketch['grounds'][number][],
  // ป่าเสาหินเมฆา: 34 spires in the box, clear of its road (the plan's `buildRegion`), plus two sea stacks.
  spires: {
    box: [920, 980, 420, 420] as const,
    n: 34,
    seed: 9,
    floor: 42,
    extra: [
      { x: 1830, y: 70, r: 14, top: 62 },
      { x: 1870, y: 95, r: 10, top: 48 },
    ],
  },
  stations: [
    { id: 'zm-village', name: 'จู๋หมิง', x: 1625, y: 640 },
    { id: 'zm-waystation', name: 'ศาลาพักม้าสิบลี้', x: 1520, y: 1060 },
    { id: 'zm-hub', name: 'ลานศิลา', x: 1500, y: 1660 },
    { id: 'zm-foot', name: 'หมู่บ้านเชิงเขา', x: 980, y: 840 },
    { id: 'zm-lake', name: 'ทะเลสาบบุปผา', x: 2140, y: 1580 },
    { id: 'zm-southpass', name: 'ด่านใต้', x: 1490, y: 2840 },
  ],
};

/**
 * The five warp points, all standing and all locked (ยังไม่เปิด): no portal, a shut gate across the way. `pass` is the
 * cut through the range (design units), `gate` where the gate stands (design units), `side` the edge it leads off.
 */
export const ZHUMING_WARPS = [
  { id: 'north', name: 'ช่องเขาเหนือ', side: 'w', pass: { x: 60, y: 620, r: 110 }, gate: [96, 632] },
  { id: 'west', name: 'ด่านตะวันตก', side: 'w', pass: { x: 40, y: 2300, r: 120 }, gate: [96, 2296] },
  { id: 'south', name: 'ด่านใต้', side: 's', pass: { x: 1470, y: 2950, r: 110 }, gate: [1470, 2890] },
  { id: 'redcliff', name: 'ด่านผาแดง', side: 's', pass: { x: 2560, y: 2900, r: 100 }, gate: [2560, 2870] },
  { id: 'ferry', name: 'เรือข้ามฟาก', side: 'n', pass: null, gate: [1478, 20] },
] as const;

/** Why every warp is shut, for the gate's sign and the map. */
export const ZHUMING_LOCKED = 'ยังไม่เปิด';

/**
 * The 12 starter facilities (OW_1KM.md): `at` in village metres (layout ข's tiles) for the eleven in the village, and
 * the summit in region metres. `landmark` is the village layout's landmark it stands at, where there is one.
 */
export const ZHUMING_FACILITIES = [
  { n: 1, id: 'stable', name: 'จุดพักม้า', at: [93, 88], landmark: 'carriage' },
  { n: 2, id: 'smith', name: 'ช่างเหล็ก', at: [32, 92] },
  { n: 3, id: 'tailor', name: 'ช่างตัดผ้า', at: [97, 77], landmark: 'shop' },
  { n: 4, id: 'carpenter', name: 'ช่างไม้', at: [52, 31] },
  { n: 5, id: 'farmer', name: 'ชาวนา', at: [50, 108] },
  { n: 6, id: 'woodcutter', name: 'ช่างตัดฟืน', at: [136, 76] },
  { n: 7, id: 'beggar', name: 'ขอทาน', at: [80, 125], landmark: 'gate' },
  { n: 8, id: 'musician', name: 'นักดนตรี', at: [104, 40] },
  { n: 9, id: 'fishing', name: 'จุดตกปลา', at: [84, 11] },
  { n: 10, id: 'inn', name: 'โรงเตี๊ยม', at: [60, 71], landmark: 'inn' },
  { n: 11, id: 'yard', name: 'โรงฝึกยุทธ', at: [46, 75], landmark: 'trainYard' },
  { n: 12, id: 'mountain', name: 'ภูเขา (ยอดเขาทอง)', region: [m(520), m(1000)] as XY },
] as const;

/** A facility's spot in region metres. */
export function zhumingFacilityAt(f: (typeof ZHUMING_FACILITIES)[number]): XY {
  return 'region' in f ? f.region : [ZHUMING_CORE.x + f.at[0], ZHUMING_CORE.y + f.at[1]];
}

/** The open country's hunting grounds (region metres), from the plan's places at 1/3. */
const WILDS: RegionWild[] = [
  { id: 'zm-bamboo', place: 7, name: 'ทะเลไผ่', rect: [m(1900), m(2200), m(760), m(1000)], spawns: [{ monster: 'wolf', n: 4 }, { monster: 'spider', n: 4 }] },
  { id: 'zm-wolves', place: 19, name: 'ทุ่งหมาป่าเงาจันทร์', rect: [m(1700), m(1900), m(1280), m(1420)], spawns: [{ monster: 'wolf', n: 6 }] },
  { id: 'zm-bandits', place: 9, name: 'ค่ายโจรภูเขา', rect: [m(780), m(940), m(1920), m(2040)], spawns: [{ monster: 'bandit', n: 6 }, { monster: 'knifer', n: 2 }] },
  { id: 'zm-graves', place: 17, name: 'สุสานใต้เขา', rect: [m(700), m(820), m(1400), m(1500)], spawns: [{ monster: 'jiangshi', n: 4 }] },
];

const mh = <T extends { h: number }>(o: T): T => ({ ...o, h: m(o.h) });

export const ZHUMING_REGION: RegionSketch = {
  id: 'zhuming',
  size: 1024,
  seed: 9,
  unit: U,
  core: { x: ZHUMING_CORE.x, y: ZHUMING_CORE.y, layout: 'yanyu' },
  coast: D.coast.map(xy),
  islands: D.islands.map((i) => ({ ...i, x: m(i.x), y: m(i.y), r: m(i.r), h: m(i.h) })),
  stoneLegs: D.stoneLegs.map(([a, b]) => [xy(a), xy(b)] as const),
  peaks: D.peaks.map((p) => ({ x: m(p.x), y: m(p.y), h: m(p.h), r: m(p.r) })),
  southRidge: { pts: D.southRidge.pts.map(xy), h: m(D.southRidge.h), reach: m(D.southRidge.reach), edgeFrom: m(D.southRidge.edgeFrom) },
  // The passes carve the range only (terrain): the warps are shut (`ZHUMING_WARPS`), so `to` names nothing reachable.
  passes: ZHUMING_WARPS.flatMap((w) => (w.pass ? [{ id: w.id, x: m(w.pass.x), y: m(w.pass.y), r: m(w.pass.r), name: w.name, to: 'zhuming' as const }] : [])),
  flats: D.flats.map((f) => ({ ...mh(f), x: m(f.x), y: m(f.y), r: m(f.r) })),
  rivers: D.rivers.map((r) => ({ name: r.name, pts: r.pts.map(xy), bed: r.bed.map(m), w: r.w.map(m) })),
  falls: D.falls.map((f) => ({ ...f, x: m(f.x), y: m(f.y), drop: m(f.drop) })),
  lakes: D.lakes.map((l) => ({ ...mh(l), x: m(l.x), y: m(l.y), rx: m(l.rx), ry: m(l.ry) })),
  cover: D.cover.map(([c, x, y, r]) => [c, m(x), m(y), m(r)] as const),
  roads: D.roads.map((r) => ({ ...r, pts: r.pts.map(xy) })),
  grounds: D.grounds.map((g) => ({ ...g, x: m(g.x), y: m(g.y), rw: m(g.rw), rh: m(g.rh) })),
  spires: {
    box: [m(D.spires.box[0]), m(D.spires.box[1]), m(D.spires.box[2]), m(D.spires.box[3])],
    n: D.spires.n,
    seed: D.spires.seed,
    floor: m(D.spires.floor),
    avoidRoad: 'stoneforest',
    extra: D.spires.extra.map((s) => ({ x: m(s.x), y: m(s.y), r: m(s.r), top: m(s.top) })),
  },
  maxHeight: m(380),
  // The shut gates on the map (data/mapLabels.ts `ZONE_LABELS.zhuming`).
  landmarks: Object.fromEntries(ZHUMING_WARPS.map((w) => [`zmGate-${w.id}`, xy([w.gate[0], w.gate[1]])])),
  stations: D.stations.map((s) => ({ ...s, x: m(s.x), y: m(s.y) })),
  // No portals: every warp point is locked (ยังไม่เปิด).
  portals: [],
  wilds: WILDS,
};

/** The landmarks the plan draws, as greybox boxes (detail lanes replace them by id), and the shut gates. */
export const ZHUMING_BLOCKOUT: readonly BlockoutBox[] = [
  ...ZHUMING_WARPS.map(
    (w): BlockoutBox => ({
      id: `warp.${w.id}`,
      place: 0,
      name: `${w.name} (${ZHUMING_LOCKED})`,
      x: m(w.gate[0]),
      y: m(w.gate[1]),
      // Across the way: a north-south gate on the west edge, an east-west one on the others.
      w: w.side === 'w' ? 3 : 12,
      d: w.side === 'w' ? 12 : 3,
      h: 7,
      kind: 'gate',
      lane: 'zhuming',
      note: 'warp point, locked',
    }),
  ),
  { id: 'lighthouse', place: 12, name: 'ประภาคารเก่า', x: m(1760), y: m(120), w: 6, d: 6, h: m(78), kind: 'tower', lane: 'zhuming' },
  { id: 'pagoda9', place: 14, name: 'เจดีย์เก้าชั้นริมเมือง', x: m(1240), y: m(1560), w: 8, d: 8, h: 21, kind: 'tier', lane: 'zhuming' },
  { id: 'seashrine', place: 21, name: 'ศาลเทพทะเล', x: m(520), y: m(210), w: 5, d: 5, h: 3.5, kind: 'house', lane: 'zhuming' },
  { id: 'summit', place: 3, name: 'ยอดเขาทอง', x: m(520), y: m(1000), w: 8, d: 8, h: 0.5, kind: 'landing', lane: 'zhuming' },
];

/**
 * The qinggong routes of the plan (scripts/layouts/open-world/region-1km.mjs ROUTES, design units scaled to metres): where a flier
 * glides from, which pillars and stepping stones link, where the cliff is climbed. Region metres. The client marks them
 * with pennant posts (render3d2/gd/zhumingSigns.ts).
 */
export type ZhumingRouteKind = 'glide' | 'spire' | 'water' | 'climb';
export const ZHUMING_ROUTES: readonly { readonly name: string; readonly kind: ZhumingRouteKind; readonly pts: readonly XY[] }[] = [
  { name: 'ร่อนจากยอดเขาทองสู่ลานศิลา', kind: 'glide', pts: [[520, 1000], [1450, 1640]].map(([x, y]) => xy([x!, y!])) },
  { name: 'ร่อนจากยอดเขาทองสู่ที่พักม้า', kind: 'glide', pts: [[520, 1000], [1440, 470]].map(([x, y]) => xy([x!, y!])) },
  { name: 'กระโดดเสาหิน', kind: 'spire', pts: [[960, 1050], [1040, 1130], [1120, 1100], [1190, 1210], [1260, 1260]].map(([x, y]) => xy([x!, y!])) },
  { name: 'วิ่งบนน้ำข้ามอ่าว (หินเหยียบ)', kind: 'water', pts: [[1560, 400], [1660, 300], [1760, 120], [2010, 170]].map(([x, y]) => xy([x!, y!])) },
  { name: 'ร่อนจากผาหัวมังกร', kind: 'glide', pts: [[1080, 140], [1300, 200], [1500, 420]].map(([x, y]) => xy([x!, y!])) },
  { name: 'ไต่หน้าผาน้ำตกเมฆา', kind: 'climb', pts: [[760, 760], [690, 790], [620, 840]].map(([x, y]) => xy([x!, y!])) },
];
